import {
    AreaChart, Area, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid, Legend, Brush
} from "recharts";
import React, { useMemo } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const HALF_DAY = 12 * 60 * 60 * 1000;
const fmt = new Intl.NumberFormat("es-MX");

// 🎨 COLORES ESTANDARIZADOS
const COLORS = {
    movil: "#10b981",   // Verde
    fijo: "#3b82f6",    // Azul
    entrantes: "#ef4444", // Rojo
    buzon: "#1e293b"    // Negro
};

// 'YYYY-MM-DD' -> fecha LOCAL
function toLocalTs(s) {
    if (!s) return null;
    const d10 = s.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(d10)) {
        const [y, m, d] = d10.split("-").map(Number);
        return new Date(y, m - 1, d).getTime();
    }
    return new Date(s).getTime();
}

export default function GraficaConteoLlamadas({ resumen, detalleData }) {
    // Intentar obtener datos de múltiples fuentes posibles
    const raw = useMemo(() => {
        // Opción 1: llamadasPorDia (ideal)
        if (resumen?.llamadasPorDia && Array.isArray(resumen.llamadasPorDia)) {
            return resumen.llamadasPorDia;
        }

        // Opción 2: datos del detalle de la tabla
        if (detalleData && Array.isArray(detalleData)) {
            const grouped = new Map();

            detalleData.forEach(row => {
                const fecha = row.fecha || row.Fecha;
                if (!fecha) return;

                const key = fecha.slice(0, 10);

                if (!grouped.has(key)) {
                    grouped.set(key, {
                        fecha: key,
                        movil: 0,
                        fijo: 0,
                        entrantes: 0
                    });
                }

                const data = grouped.get(key);
                data.movil += Number(row.llamadas_movil || row['Llam. Móvil'] || 0);
                data.fijo += Number(row.llamadas_fijo || row['Llam. Fijo'] || 0);
                data.entrantes += Number(row.llamadas_entrantes || row['Llam. Ent.'] || 0);
            });

            return Array.from(grouped.values());
        }

        // Opción 3: Si solo hay resumen agregado
        if (resumen && (resumen.llamadas_movil || resumen.llamadas_fijo)) {
            return [{
                fecha: new Date().toISOString().slice(0, 10),
                movil: Number(resumen.llamadas_movil || 0),
                fijo: Number(resumen.llamadas_fijo || 0),
                entrantes: Number(resumen.llamadas_entrantes || 0)
            }];
        }

        return [];
    }, [resumen, detalleData]);

    // agrupado por día
    const grouped = useMemo(() => {
        const acc = new Map();

        for (const d of raw) {
            if (!d?.fecha) continue;

            const ts = toLocalTs(d.fecha);
            if (isNaN(ts)) continue;

            const key = format(new Date(ts), "yyyy-MM-dd");
            const prev = acc.get(key) || { fecha: key, ts, movil: 0, fijo: 0, entrantes: 0 };

            prev.movil += Number(d.movil || 0);
            prev.fijo += Number(d.fijo || 0);
            prev.entrantes += Number(d.entrantes || 0);

            acc.set(key, prev);
        }

        return [...acc.values()].sort((a, b) => a.ts - b.ts);
    }, [raw]);

    const isSingleDay = grouped.length === 1;

    const displayData = useMemo(() => {
        if (grouped.length === 0) return [];

        if (!isSingleDay) return grouped;

        const d = grouped[0];
        return [
            { ts: d.ts - HALF_DAY, movil: 0, fijo: 0, entrantes: 0, ghost: true },
            d,
            { ts: d.ts + HALF_DAY, movil: 0, fijo: 0, entrantes: 0, ghost: true },
        ];
    }, [grouped, isSingleDay]);

    const ticks = useMemo(() => {
        if (grouped.length === 0) return [];
        if (isSingleDay) return [grouped[0].ts];
        return grouped.map(d => d.ts);
    }, [grouped, isSingleDay]);

    const xDomain = useMemo(() => {
        if (grouped.length === 0) return [0, 1];

        if (isSingleDay) {
            const t = grouped[0].ts;
            return [t - HALF_DAY, t + HALF_DAY];
        }

        const min = grouped[0]?.ts ?? 0;
        const max = grouped[grouped.length - 1]?.ts ?? 0;
        const pad = (max - min) * 0.02;
        return [min - pad, max + pad];
    }, [grouped, isSingleDay]);

    const stats = useMemo(() => {
        const maxMovil = Math.max(0, ...grouped.map(d => Number(d.movil || 0)));
        const maxFijo = Math.max(0, ...grouped.map(d => Number(d.fijo || 0)));
        const maxEntrantes = Math.max(0, ...grouped.map(d => Number(d.entrantes || 0)));

        const maxSalientes = Math.max(maxMovil, maxFijo);
        const useSingleAxis = maxEntrantes > 0 && (maxEntrantes / maxSalientes) > 0.1 && (maxEntrantes / maxSalientes) < 10;

        return {
            maxMovil,
            maxFijo,
            maxEntrantes,
            maxSalientes,
            useSingleAxis
        };
    }, [grouped]);

    if (!raw || raw.length === 0 || grouped.length === 0) {
        return (
            <div className="h-[280px] w-full bg-white shadow rounded-xl p-3 flex items-center justify-center">
                <div className="text-center">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p className="text-xs font-semibold text-gray-600">No hay datos de llamadas por día</p>
                    <p className="text-[11px] text-gray-500 mt-1">Selecciona un rango de fechas con datos</p>
                </div>
            </div>
        );
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;

        return (
            <div className="bg-white p-2 shadow-lg rounded-lg border border-gray-200">
                <p className="text-[11px] font-semibold text-gray-700 mb-1">
                    {format(new Date(label), "d 'de' MMMM, yyyy", { locale: es })}
                </p>
                <div className="space-y-0.5">
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-1.5">
                                <div
                                    className="w-2.5 h-2.5 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-[10px] text-gray-600">{entry.name}:</span>
                            </div>
                            <span className="text-[10px] font-semibold text-gray-900">
                                {fmt.format(entry.value)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="h-[280px] w-full bg-white shadow rounded-xl p-3">
            <div className="text-center mb-2">
                <p className="text-xs font-semibold text-gray-800">Llamadas por día</p>
                <p className="text-[10px] text-gray-500">
                    Comparativa diaria entre móvil, fijo y entrantes
                </p>
            </div>

            <ResponsiveContainer width="100%" height="87%">
                <AreaChart data={displayData} margin={{ top: 5, right: stats.useSingleAxis ? 10 : 35, bottom: 25, left: 0 }}>
                    <defs>
                        <linearGradient id="colorMovil" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.movil} stopOpacity={0.5} />
                            <stop offset="95%" stopColor={COLORS.movil} stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="colorFijo" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.fijo} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={COLORS.fijo} stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="colorEntrantes" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.entrantes} stopOpacity={0.5} />
                            <stop offset="95%" stopColor={COLORS.entrantes} stopOpacity={0.05} />
                        </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

                    <XAxis
                        type="number"
                        dataKey="ts"
                        domain={xDomain}
                        scale="time"
                        ticks={ticks}
                        tickMargin={6}
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        tickFormatter={(ts) => format(new Date(ts), isSingleDay ? "d MMM" : "d MMM", { locale: es })}
                    />

                    {stats.useSingleAxis ? (
                        <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 10, fill: '#6b7280' }}
                            tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                            domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
                        />
                    ) : (
                        <>
                            <YAxis
                                yAxisId="left"
                                allowDecimals={false}
                                tick={{ fontSize: 10, fill: '#6b7280' }}
                                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                                domain={[0, (dataMax) => Math.ceil(Math.max(dataMax, stats.maxSalientes) * 1.15)]}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                allowDecimals={false}
                                tick={{ fontSize: 10, fill: COLORS.entrantes }}
                                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                                domain={[0, (dataMax) => Math.ceil(Math.max(dataMax, stats.maxEntrantes) * 1.15)]}
                            />
                        </>
                    )}

                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                        wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }}
                        iconType="circle"
                        iconSize={8}
                    />

                    {/* Móvil (verde) */}
                    <Area
                        yAxisId={stats.useSingleAxis ? undefined : "left"}
                        type="monotone"
                        dataKey="movil"
                        name="Móvil"
                        stroke={COLORS.movil}
                        strokeWidth={2}
                        fill="url(#colorMovil)"
                        dot={{ r: 3, fill: COLORS.movil, strokeWidth: 1.5, stroke: '#fff' }}
                        activeDot={{ r: 5 }}
                        connectNulls
                        isAnimationActive={true}
                        animationDuration={800}
                    />

                    {/* Fijo (azul) */}
                    <Area
                        yAxisId={stats.useSingleAxis ? undefined : "left"}
                        type="monotone"
                        dataKey="fijo"
                        name="Fijo"
                        stroke={COLORS.fijo}
                        strokeWidth={2}
                        fill="url(#colorFijo)"
                        dot={{ r: 3, fill: COLORS.fijo, strokeWidth: 1.5, stroke: '#fff' }}
                        activeDot={{ r: 5 }}
                        connectNulls
                        isAnimationActive={true}
                        animationDuration={800}
                    />

                    {/* Entrantes (rojo) */}
                    <Area
                        yAxisId={stats.useSingleAxis ? undefined : "right"}
                        type="monotone"
                        dataKey="entrantes"
                        name="Entrantes"
                        stroke={COLORS.entrantes}
                        strokeWidth={2}
                        fill="url(#colorEntrantes)"
                        dot={{ r: 3, fill: COLORS.entrantes, strokeWidth: 1.5, stroke: '#fff' }}
                        activeDot={{ r: 5 }}
                        connectNulls
                        isAnimationActive={true}
                        animationDuration={800}
                    />

                    {!isSingleDay && grouped.length > 3 && (
                        <Brush
                            dataKey="ts"
                            height={14}
                            travellerWidth={7}
                            stroke="#94a3b8"
                            fill="#f1f5f9"
                            tickFormatter={(ts) => format(new Date(ts), "d MMM", { locale: es })}
                        />
                    )}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}